"use strict";
import { ReactComponent as TooltipTriangle } from "assets/svg/tooltip_triangle.svg";
import useCopyClipboard from "hooks/useCopyClipboard";
import styled from "lib/styled-components";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { CheckCircle, Copy } from "react-feather";
import { Trans } from "react-i18next";
import { ClickableStyle, EllipsisStyle } from "theme/components/styles";
import { Z_INDEX } from "theme/zIndex";
import { Flex, isTouchable } from "ui/src";
const TOOLTIP_WIDTH = 60;
const ToolTipWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  left: ${({ isCopyContractTooltip, tooltipX }) => isCopyContractTooltip && (tooltipX ? `${tooltipX - TOOLTIP_WIDTH / 2}px` : "50%")};
  transform: translate(5px, 32px);
  z-index: ${Z_INDEX.tooltip};
`;
const StyledTooltipTriangle = styled(TooltipTriangle)`
  path {
    fill: ${({ theme }) => theme.black};
  }
`;
const CopiedTooltip = styled.div`
  background-color: ${({ theme }) => theme.black};
  text-align: center;
  justify-content: center;
  width: ${({ isCopyContractTooltip }) => !isCopyContractTooltip && `${TOOLTIP_WIDTH}px`};
  height: ${({ isCopyContractTooltip }) => !isCopyContractTooltip && "32px"};
  line-height: ${({ isCopyContractTooltip }) => !isCopyContractTooltip && "32px"};

  padding: ${({ isCopyContractTooltip }) => isCopyContractTooltip && "8px"};
  border-radius: 8px;

  color: ${({ theme }) => theme.white};
  font-size: 12px;
`;
function Tooltip({ isCopyContractTooltip, tooltipX }) {
  return <ToolTipWrapper isCopyContractTooltip={isCopyContractTooltip} tooltipX={tooltipX}><StyledTooltipTriangle /><CopiedTooltip isCopyContractTooltip={isCopyContractTooltip}>Copied</CopiedTooltip></ToolTipWrapper>;
}
const CopyIconWrapper = styled.div`
  text-decoration: none;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  display: flex;
`;
export function CopyToClipboard({ toCopy, children }) {
  const [isCopied, setCopied] = useCopyClipboard();
  const copy = useCallback(() => {
    setCopied(toCopy);
  }, [toCopy, setCopied]);
  return <CopyIconWrapper onClick={copy}>{children}{isCopied && <Tooltip isCopyContractTooltip={false} />}</CopyIconWrapper>;
}
const CopyHelperContainer = styled.div`
  ${ClickableStyle}
  display: flex;
  flex-direction: row;
  gap: ${({ gap }) => gap + "px"};
  align-items: center;
  color: ${({ color }) => color ?? "inherit"};
`;
const CopyHelperText = styled.div`
  ${EllipsisStyle}
  ${({ fontSize }) => fontSize ? "font-size: " + fontSize + "px" : "inherit"};
  max-width: calc(100% - ${({ offset }) => offset + "px"});
`;
const StyledCheckCircle = styled(CheckCircle)`
  color: ${({ theme }) => theme.success};
  stroke-width: 1.5px;
`;
function isEllipsisActive(element) {
  return Boolean(element && element.offsetWidth < element.scrollWidth);
}
export const CopyHelper = forwardRef(
  ({
    InitialIcon = Copy,
    CopiedIcon = StyledCheckCircle,
    toCopy,
    color,
    fontSize,
    iconSize = 20,
    gap = 4,
    iconPosition = "left",
    iconColor = "currentColor",
    children
  }, ref) => {
    const [isCopied, setCopied] = useCopyClipboard();
    const copy = useCallback(() => {
      setCopied(toCopy);
    }, [toCopy, setCopied]);
    useImperativeHandle(ref, () => ({
      forceCopy() {
        copy();
      }
    }));
    const textRef = useRef(null);
    const isEllipsis = isEllipsisActive(textRef.current);
    const displayGap = isEllipsis ? gap - 4 : gap;
    const [isHover, setIsHover] = useState(false);
    const onHover = useCallback(() => setIsHover(true), []);
    const offHover = useCallback(() => setIsHover(false), []);
    const showIcon = Boolean(iconPosition === "left" || isHover || isTouchable || isCopied);
    const Icon2 = isCopied ? CopiedIcon : showIcon ? InitialIcon : null;
    const offset = showIcon ? gap + iconSize : 0;
    return <CopyHelperContainer
      onClick={copy}
      color={color}
      clicked={isCopied}
      gap={displayGap}
      onMouseEnter={onHover}
      onMouseLeave={offHover}
    >{iconPosition === "left" && Icon2 && <Icon2 size={iconSize} strokeWidth={1.5} color={iconColor} />}<CopyHelperText ref={textRef} fontSize={fontSize} offset={offset}>{isCopied && iconPosition === "left" ? <Trans i18nKey="common.copied" /> : children}</CopyHelperText><Flex $platform-web={{ clear: "both" }} />{iconPosition === "right" && Icon2 && <Icon2 size={iconSize} strokeWidth={1.5} color={iconColor} />}</CopyHelperContainer>;
  }
);
CopyHelper.displayName = "CopyHelper";
